import React, { useState } from 'react';
import {
  Pane,
  Heading,
  Button,
  Text,
  Alert,
  majorScale,
} from 'evergreen-ui';
import FileUpload from './FileUpload';
import DelimiterSelector from './DelimiterSelector';
import FieldMapper from './FieldMapper';
import ValidationResults from './ValidationResults';
import DuplicateResolver from './DuplicateResolver';
import ImportProgress from './ImportProgress';
import ImportSummary from './ImportSummary';
import { Steps } from './common';
import { CsvImportProvider } from './context/CsvImportContext';

const STEPS = {
  UPLOAD: 0,
  MAPPING: 1,
  VALIDATION: 2,
  DUPLICATES: 3,
  IMPORT: 4,
  SUMMARY: 5,
};

const CsvImportPage = ({ entityType }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [importSession, setImportSession] = useState(null);
  const [error, setError] = useState(null);

  const handleStepComplete = (step, data) => {
    switch (step) {
      case STEPS.UPLOAD:
        setImportSession(data);
        setCurrentStep(STEPS.MAPPING);
        break;
      case STEPS.MAPPING:
        setCurrentStep(STEPS.VALIDATION);
        break;
      case STEPS.VALIDATION:
        setCurrentStep(STEPS.DUPLICATES);
        break;
      case STEPS.DUPLICATES:
        setCurrentStep(STEPS.IMPORT);
        break;
      case STEPS.IMPORT:
        setCurrentStep(STEPS.SUMMARY);
        break;
      default:
        break;
    }
  };

  const handleError = (error) => {
    setError(error);
    window.scrollTo(0, 0);
  };

  const renderStep = () => {
    if (!importSession && currentStep !== STEPS.UPLOAD) {
      return null;
    }

    switch (currentStep) {
      case STEPS.UPLOAD:
        return (
          <FileUpload
            entityType={entityType}
            onComplete={(data) => handleStepComplete(STEPS.UPLOAD, data)}
            onError={handleError}
          />
        );
      case STEPS.MAPPING:
        return (
          <FieldMapper
            importSession={importSession}
            onComplete={() => handleStepComplete(STEPS.MAPPING)}
            onError={handleError}
          />
        );
      case STEPS.VALIDATION:
        return (
          <ValidationResults
            importSession={importSession}
            onComplete={() => handleStepComplete(STEPS.VALIDATION)}
            onError={handleError}
          />
        );
      case STEPS.DUPLICATES:
        return (
          <DuplicateResolver
            importSession={importSession}
            onComplete={() => handleStepComplete(STEPS.DUPLICATES)}
            onError={handleError}
          />
        );
      case STEPS.IMPORT:
        return (
          <ImportProgress
            importSession={importSession}
            onComplete={() => handleStepComplete(STEPS.IMPORT)}
            onError={handleError}
          />
        );
      case STEPS.SUMMARY:
        return (
          <ImportSummary
            importSession={importSession}
            onError={handleError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CsvImportProvider>
      <Pane padding={majorScale(2)}>
        <Heading size={900} marginBottom={majorScale(2)}>
          Import {entityType}s
        </Heading>

        {error && (
          <Alert
            intent="danger"
            title="Error"
            marginBottom={majorScale(2)}
            onRemove={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Pane marginBottom={majorScale(3)}>
          <Steps
            currentStep={currentStep}
            steps={[
              { title: 'Upload File' },
              { title: 'Map Fields' },
              { title: 'Validate Data' },
              { title: 'Resolve Duplicates' },
              { title: 'Import Data' },
              { title: 'Summary' },
            ]}
          />
        </Pane>

        {renderStep()}
      </Pane>
    </CsvImportProvider>
  );
};

export default CsvImportPage;
